import React from 'react';
import { COLORS } from '../../globalStyles';
import { diamondPoints } from '../../utils';

interface SignatureMarkProps {
  size?: number;
  title?: string;
  /** Stroke/fill colour. Defaults to the brand accent. */
  color?: string;
  /** Ring line weight, in screen px (non-scaling). */
  strokeWidth?: number;
}

// A static replica of the hero signature: the same six concentric diamond
// outlines (center 340,340; a ring at value v spans v → 680-v) with a solid
// core, fading outward like the signature. The innermost ring is anchored at
// 1.0 and each ring outward steps down evenly to 0.32 at the outermost — a
// strong, even gradient (knob: OUTER_OPACITY). Lower it for a stronger outward
// fade; the linear step keeps the ring-to-ring proportions uniform. Strokes use
// non-scaling-stroke to stay a fixed weight.
const RINGS = [40, 92, 144, 196, 248, 300];
const OUTER_OPACITY = 0.32; // outermost ring opacity; inner anchored at 1.0
const baseOpacity = (i: number) =>
  OUTER_OPACITY + (i / (RINGS.length - 1)) * (1 - OUTER_OPACITY);

const SignatureMark: React.FC<SignatureMarkProps> = ({
  size = 24,
  title,
  color = COLORS.accent,
  strokeWidth = 0.5,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 680 680"
    xmlns="http://www.w3.org/2000/svg"
    role={title ? 'img' : 'presentation'}
    aria-hidden={title ? undefined : true}
    aria-label={title}
    style={{ display: 'block', flexShrink: 0, color, overflow: 'visible' }}
  >
    {title && <title>{title}</title>}
    {RINGS.map((v, i) => (
      <polygon
        key={v}
        points={diamondPoints(v)}
        fill="none"
        stroke="currentColor"
        strokeOpacity={baseOpacity(i)}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />
    ))}
    {/* Solid core diamond — the brightest point */}
    <polygon points="340,322 358,340 340,358 322,340" fill="currentColor" fillOpacity={1} />
  </svg>
);

export default SignatureMark;
